import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import RefreshIcon from '@material-ui/icons/Refresh'

import Widget from '../../components/Widget'
import {
    createAppointmentDuration,
    deleteAppointmentDuration,
    updateAppointmentDuration,
    useManagementAppointmentDurations,
} from '../../apis'
import { useApiHttpState } from '../../context/ApiContext'
import {
    createIconActionName,
    DestructiveConfirmationDialog,
    FieldValidationError,
    fieldErrorAttributes,
    SectionFeedback,
    useSnackbarSectionInteraction,
} from './primitives'

const SECTION_ID = 'appointment-durations'

export const orderAppointmentDurations = records => [...records].sort((left, right) =>
    Number(left.minutes) - Number(right.minutes) || Number(left.id) - Number(right.id))

export const validateAppointmentDuration = (value, records = [], editedId = null) => {
    const raw = String(value === undefined || value === null ? '' : value).trim()
    if (!/^\d+$/.test(raw) || Number(raw) <= 0) return 'Enter a whole number of minutes greater than 0.'
    const minutes = Number(raw)
    const duplicate = records.some(record =>
        Number(record.id) !== Number(editedId) && Number(record.minutes) === minutes)
    return duplicate ? 'An appointment duration with these minutes already exists. Choose a different duration.' : ''
}

const conflictGuidance = error => {
    if (!error) return ''
    if (error.code === 'DUPLICATE_DURATION') return 'An appointment duration with these minutes already exists. Choose a different duration.'
    if (error.code === 'CONFIGURATION_IN_USE') return 'This duration is used by existing appointments. Create another duration instead of changing it.'
    return ''
}
export default function AppointmentDurationsSection() {
    const source = useManagementAppointmentDurations()
    const { api } = useApiHttpState()
    const interaction = useSnackbarSectionInteraction(source.managementSection || null)
    const { state } = interaction
    const observed = useRef(source.managementSection || null)
    const observedError = useRef(null)
    const headingRef = useRef(null)
    const deleteOpenerRef = useRef(null)
    const [minutes, setMinutes] = useState('')
    const [editing, setEditing] = useState(null)
    const [editorOpen, setEditorOpen] = useState(false)
    const [clientError, setClientError] = useState('')
    const [deleting, setDeleting] = useState(null)

    useEffect(() => {
        if (source.managementSection && source.managementSection !== observed.current) {
            observed.current = source.managementSection
            interaction.load(() => source.managementSection)
        } else if (source.isError && source.isError !== observedError.current) {
            observedError.current = source.isError
            interaction.load(() => Promise.reject(source.isError))
        }
    }, [interaction, source.isError, source.managementSection])

    const revalidate = useCallback(() => source.revalidate(), [source.revalidate])
    const records = state.confirmedData ? orderAppointmentDurations(state.confirmedData.records) : []
    const versionToken = state.confirmedData && state.confirmedData.versionToken
    const fieldError = clientError || state.fieldErrors.minutes || conflictGuidance(state.mutationError)
    const controlsDisabled = !interaction.canSubmit

    const resetEditor = () => {
        setEditing(null)
        setEditorOpen(false)
        setMinutes('')
        setClientError('')
        interaction.setDraft(null)
    }

    const beginCreate = () => {
        interaction.clearFeedback()
        setEditing(null)
        setEditorOpen(true)
        setMinutes('')
        setClientError('')
        interaction.setDraft({ mode: 'create', id: null, minutes: '' })
    }

    const beginEdit = record => {
        interaction.clearFeedback()
        setEditing(record)
        setEditorOpen(true)
        setMinutes(String(record.minutes))
        setClientError('')
        interaction.setDraft({ mode: 'edit', id: record.id, minutes: String(record.minutes) })
    }

    const submit = async event => {
        event.preventDefault()
        const validationError = validateAppointmentDuration(minutes, records, editing && editing.id)
        setClientError(validationError)
        if (validationError) return
        const normalizedMinutes = Number(minutes.trim())
        const draft = { mode: editing ? 'edit' : 'create', id: editing && editing.id, minutes }
        interaction.setDraft(draft)
        const result = await interaction.submit({
            mutation: () => editing
                ? updateAppointmentDuration(api, editing.id, { minutes: normalizedMinutes }, versionToken)
                : createAppointmentDuration(api, { minutes: normalizedMinutes }, versionToken),
            refresh: revalidate,
            draft,
            mutationKey: editing ? `edit:${editing.id}` : 'create',
            successMessage: editing
                ? `Appointment duration changed to ${normalizedMinutes} minutes.`
                : `Appointment duration ${normalizedMinutes} minutes created.`,
        })
        if (result.ok) resetEditor()
    }

    const requestDelete = (record, event) => {
        interaction.clearFeedback()
        deleteOpenerRef.current = event.currentTarget
        setDeleting(record)
    }
    const confirmDelete = () => interaction.submit({
        mutation: () => deleteAppointmentDuration(api, deleting.id, versionToken),
        refresh: revalidate,
        mutationKey: `delete:${deleting.id}`,
        successMessage: `Appointment duration ${deleting.minutes} minutes deleted.`,
    })

    return (
        <section aria-labelledby={`${SECTION_ID}-heading`}>
            <Widget
                disableWidgetMenu
                header={
                    <React.Fragment>
                        <Typography
                            ref={headingRef}
                            id={`${SECTION_ID}-heading`}
                            component="h2"
                            variant="h5"
                            color="textSecondary"
                            tabIndex={-1}
                        >
                            Appointment Durations
                        </Typography>
                        <div>
                            <IconButton
                                type="button"
                                aria-label="Refresh Appointment Durations"
                                onClick={() => interaction.refresh(revalidate)}
                                disabled={controlsDisabled}
                            >
                                <RefreshIcon />
                            </IconButton>
                            <Button
                                type="button"
                                color="primary"
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={beginCreate}
                                disabled={controlsDisabled}
                            >
                                Add appointment duration
                            </Button>
                        </div>
                    </React.Fragment>
                }
            >
                <SectionFeedback
                    state={state}
                    sectionName="appointment durations"
                    onRetryLoad={() => interaction.load(revalidate)}
                    onRetryRefresh={() => interaction.refresh(revalidate)}
                    onAcceptStale={interaction.acceptStale}
                />
                {state.confirmedData && (
                    <React.Fragment>
                        {records.length === 0 ? (
                            <p>No appointment durations are configured.</p>
                        ) : (
                            <Table size="small" aria-label="Appointment durations">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Duration</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {records.map(record => {
                                        const label = `${record.minutes} minutes`
                                        return (
                                            <TableRow key={record.id}>
                                                <TableCell>{label}</TableCell>
                                                <TableCell align="right">
                                                    <IconButton
                                                        type="button"
                                                        aria-label={createIconActionName('Edit appointment duration', label)}
                                                        onClick={() => beginEdit(record)}
                                                        disabled={controlsDisabled}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        type="button"
                                                        aria-label={createIconActionName('Delete appointment duration', label)}
                                                        onClick={event => requestDelete(record, event)}
                                                        disabled={controlsDisabled}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        )}
                        <Dialog
                            open={editorOpen}
                            onClose={controlsDisabled ? undefined : resetEditor}
                            aria-labelledby={`${SECTION_ID}-editor-title`}
                            fullWidth
                            maxWidth="sm"
                        >
                            <DialogTitle id={`${SECTION_ID}-editor-title`}>
                                {editing ? `Edit ${editing.minutes} minutes` : 'Add appointment duration'}
                            </DialogTitle>
                            <form onSubmit={submit} noValidate aria-label={editing ? `Edit appointment duration ${editing.minutes} minutes` : 'Create appointment duration'}>
                                <DialogContent>
                                    <TextField
                                        autoFocus
                                        id={`${SECTION_ID}-minutes`}
                                        label="Duration in minutes"
                                        type="number"
                                        value={minutes}
                                        onChange={event => {
                                            setMinutes(event.target.value)
                                            setClientError('')
                                            interaction.setDraft({ mode: editing ? 'edit' : 'create', id: editing && editing.id, minutes: event.target.value })
                                        }}
                                        inputProps={{ min: 1, step: 1, ...fieldErrorAttributes({ sectionId: SECTION_ID, fieldName: 'minutes', error: fieldError }) }}
                                        error={Boolean(fieldError)}
                                        disabled={controlsDisabled}
                                        required
                                        fullWidth
                                    />
                                    <FieldValidationError sectionId={SECTION_ID} fieldName="minutes" error={fieldError} />
                                </DialogContent>
                                <DialogActions>
                                    <Button type="button" onClick={resetEditor} disabled={controlsDisabled}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" color="primary" variant="contained" disabled={controlsDisabled}>
                                        {editing ? 'Save appointment duration' : 'Add appointment duration'}
                                    </Button>
                                </DialogActions>
                            </form>
                        </Dialog>
                    </React.Fragment>
                )}
            </Widget>
            <DestructiveConfirmationDialog
                open={Boolean(deleting)}
                title="Delete appointment duration?"
                recordLabel={deleting ? `${deleting.minutes} minutes` : ''}
                consequence="This permanently removes the duration from future appointment forms. A duration used by any existing appointment cannot be deleted."
                confirmLabel="Delete appointment duration"
                onConfirm={confirmDelete}
                onClose={() => setDeleting(null)}
                onConfirmed={() => setDeleting(null)}
                openerRef={deleteOpenerRef}
                sectionHeadingRef={headingRef}
            />
        </section>
    )
}
